import React, {useState, useMemo, useCallback} from "react";
import {Text, Tabs, TabsList, TabsTrigger, TabsContent, Stack, Spinner} from "@nike/podium";
import {generateMonthOptions} from "../../common/data/imbalanceHelpers";
import {DirectionFilter, StatusFilter} from "../../common/data/types";
import {ImbalanceFilter} from "../../graphql/audit-overview-api/api";
import {useImbalances} from "../../common/hooks/useImbalances";
import {useDelayedValue} from "../../common/hooks/useDelayedValue";
import {usePagination} from "../../common/hooks/usePagination";
import {useBucketActions} from "../../common/hooks/useBucketActions";
import {useExportPersistentImbalances} from "../../common/hooks/useExportPersistentImbalances";
import {FilterDrawer} from "./FilterDrawer";
import {SummaryStrip} from "./SummaryStrip";
import {ImbalanceTable} from "./ImbalanceTable";
import {ImbalanceFooter} from "./ImbalanceFooter";
import {CleanupsAdjustAll} from "../cleanups/CleanupsAdjustAll";
import "./ImbalanceManagement.css";

const DIR_TO_FILTER: Record<DirectionFilter, ImbalanceFilter | undefined> = {
    all: undefined,
    zero: ImbalanceFilter.WMS_ZERO,
    over: ImbalanceFilter.WMS_MORE_THAN_CI,
    under: ImbalanceFilter.WMS_LESS_THAN_CI,
};

export function ImbalanceManagement() {
    const [activeTab, setActiveTab] = useState<string>('t1');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const {handleExport, isExporting} = useExportPersistentImbalances();

    // --- Filters ---
    const [dir, setDir] = useState<DirectionFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [threshold, setThreshold] = useState(3);
    const [persistOnly, setPersistOnly] = useState(true);
    const [excludeSpecial, setExcludeSpecial] = useState(true);
    const [searchCode, setSearchCode] = useState('');
    const [searchSize, setSearchSize] = useState('');

    const delayedCode = useDelayedValue(searchCode, 400);
    const delayedSize = useDelayedValue(searchSize, 400);

    const monthOptions = useMemo(() => generateMonthOptions(), []);
    const selectedMonth = useMemo(() => {
        const opt = monthOptions.find(o => o.threshold === threshold);
        return opt ?? monthOptions[0];
    }, [monthOptions, threshold]);

    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab);
        if (tab === 't1') {
            setRefreshKey(prev => prev + 1);
        }
    }, []);

    // --- Data fetching ---
    const {data: imbalances, totalCount, loading, loadingMore, hasMore, error, loadMoreError, loadMore} = useImbalances(
        selectedMonth.value,
        persistOnly || undefined,
        DIR_TO_FILTER[dir],
        delayedCode.trim(),
        delayedSize.trim(),
        excludeSpecial || undefined,
        statusFilter,
        refreshKey,
    );

    // --- Pagination ---
    const {
        currentPage, totalPages, rangeStart, rangeEnd,
        pageItems, reachedEnd, tableAreaRef, handlePageChange,
    } = usePagination(
        imbalances, hasMore, loadingMore, loadMoreError, loadMore,
        [delayedCode, delayedSize, dir, threshold, persistOnly, excludeSpecial, statusFilter, refreshKey],
    );

    // --- Bucket actions (tab 2) ---
    const {
        bucketData, bucketFilters, actionedSkus, totalBucketCount, batchProgress, clearBatchProgress,
        setBucketFilter, suggestCleanups, performAction,
    } = useBucketActions(imbalances, useCallback(() => setActiveTab('t2'), []));

    const hasActiveFilters = dir !== 'all' || statusFilter !== 'all' || searchCode.trim() !== '' || searchSize.trim() !== '';

    return (
        <div className="im-page-root">
            <Stack horizontal align="baseline" gap="m" className="im-page-head">
                <Text font="display-3" as="h3">IMBALANCE MANAGEMENT</Text>
                <Text font="body-4" as="span" className="im-elc-tag">ELC</Text>
            </Stack>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="im-tabs-root">
                <Stack horizontal align="end" justify="between" className="im-toolbar">
                    <TabsList>
                        <TabsTrigger value="t1">Historic Imbalances</TabsTrigger>
                        <TabsTrigger value="t2">
                            Cleanups &amp; Adjust All
                            {totalBucketCount > 0 && (
                                <Text font="body-4" as="span" className="im-tab-count">{totalBucketCount}</Text>
                            )}
                        </TabsTrigger>
                    </TabsList>
                    <div style={{visibility: activeTab === 't1' ? 'visible' : 'hidden'}}>
                        <button
                            className={`im-filter-btn ${hasActiveFilters ? 'has-filters' : ''}`}
                            onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                        >
                            Filter
                            {hasActiveFilters && <span className="im-filter-dot"/>}
                            <span className="im-filter-caret">{filterDrawerOpen ? '▲' : '▼'}</span>
                        </button>
                    </div>
                </Stack>

                <TabsContent value="t1" className="im-tab-panel">
                    <FilterDrawer
                        open={filterDrawerOpen}
                        dir={dir}
                        onSetDir={setDir}
                        statusFilter={statusFilter}
                        onSetStatusFilter={setStatusFilter}
                        threshold={threshold}
                        onSetThreshold={setThreshold}
                        monthOptions={monthOptions}
                        persistOnly={persistOnly}
                        onTogglePersist={() => setPersistOnly(!persistOnly)}
                        excludeSpecial={excludeSpecial}
                        onToggleExcludeSpecial={() => setExcludeSpecial(!excludeSpecial)}
                        searchCode={searchCode}
                        onSearchCodeChange={setSearchCode}
                        searchSize={searchSize}
                        onSearchSizeChange={setSearchSize}
                    />

                    {loading ? (
                        <Stack align="center" justify="center" style={{padding: 64}}>
                            <Spinner />
                            <Text font="body-3" color="secondary">Loading imbalances…</Text>
                        </Stack>
                    ) : error ? (
                        <Stack align="center" justify="center" style={{padding: 64}}>
                            <Text font="body-3" color="critical">Error: {error}</Text>
                        </Stack>
                    ) : (
                        <>
                            <SummaryStrip items={imbalances} monthLabel={selectedMonth.label} totalCount={totalCount} persistOnly={persistOnly} />
                            <ImbalanceTable
                                pageItems={pageItems}
                                tableAreaRef={tableAreaRef}
                            />
                            <ImbalanceFooter
                                lineCount={imbalances.length}
                                onExport={handleExport}
                                isExporting={isExporting}
                                onSuggestCleanups={suggestCleanups}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                rangeStart={rangeStart}
                                rangeEnd={rangeEnd}
                                totalItems={imbalances.length}
                                hasMore={hasMore}
                                loadingMore={loadingMore}
                                loadMoreError={loadMoreError}
                                reachedEnd={reachedEnd}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </TabsContent>

                <TabsContent value="t2" className="im-tab-panel">
                    <CleanupsAdjustAll
                        bucketData={bucketData}
                        bucketFilters={bucketFilters}
                        onBucketFilterChange={setBucketFilter}
                        onPerformAction={performAction}
                        onClearBatchProgress={clearBatchProgress}
                        actionedSkus={actionedSkus}
                        batchProgress={batchProgress}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
